import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { STUDIO, ALL_SERVICES } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", service: "", preferred_date: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Request received — we'll be in touch very soon.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or DM us on Instagram.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-[#C9C2B1] py-3 text-[#1A1A18] placeholder:text-[#A49E90] focus:outline-none focus:border-[#1A1A18] transition-colors duration-300";

  return (
    <section id="contact" className="py-24 sm:py-36 bg-[#DFD9C9]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] text-[#7A766C] mb-6">— Bookings</p>
            <h2 className="font-serif font-light text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#1A1A18]">
              Request your <span className="italic text-[#3E3B32]">appointment</span>
            </h2>
            <p className="text-[#4A4843] leading-relaxed mt-8 max-w-md">
              Leave your details and preferred service — we'll confirm a time with you personally.
              Prefer to chat? Reach us directly below.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 space-y-4 text-[#1A1A18]">
              <a href={`tel:${STUDIO.phone}`} data-testid="contact-phone" className="block group">
                <span className="text-xs uppercase tracking-[0.2em] text-[#7A766C] block mb-1">Phone</span>
                <span className="font-serif text-2xl group-hover:text-[#3E3B32] transition-colors">{STUDIO.phoneDisplay}</span>
              </a>
              <a href={STUDIO.instagramUrl} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="block group">
                <span className="text-xs uppercase tracking-[0.2em] text-[#7A766C] block mb-1">Instagram</span>
                <span className="font-serif text-2xl group-hover:text-[#3E3B32] transition-colors">{STUDIO.instagram}</span>
              </a>
              <a href={`mailto:${STUDIO.email}`} data-testid="contact-email" className="block group">
                <span className="text-xs uppercase tracking-[0.2em] text-[#7A766C] block mb-1">Email</span>
                <span className="font-serif text-2xl group-hover:text-[#3E3B32] transition-colors break-all">{STUDIO.email}</span>
              </a>
              <div data-testid="contact-location">
                <span className="text-xs uppercase tracking-[0.2em] text-[#7A766C] block mb-1">Location</span>
                <span className="font-serif text-2xl">Boronia, Melbourne</span>
              </div>
              <div data-testid="contact-hours" className="pt-2 border-t border-[#C9C2B1]/60 mt-2">
                <span className="text-xs uppercase tracking-[0.2em] text-[#7A766C] block mb-1">Hours</span>
                <span className="font-serif text-2xl block leading-snug">Monday – Sunday</span>
                <span className="font-serif text-2xl block leading-snug">10:00 AM – 8:00 PM</span>
                <span className="text-sm text-[#7A766C] block mt-1">By appointment only</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="bg-[#F8F6F0] rounded-[2rem] p-7 sm:p-12 border border-[#D5CFC1]"
            data-testid="appointment-form"
          >
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A766C]">Name</label>
                <input className={field} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="input-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A766C]">Phone</label>
                <input className={field} value={form.phone} onChange={set("phone")} placeholder="Mobile number" data-testid="input-phone" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A766C]">Email</label>
                <input type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="input-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A766C]">Preferred date</label>
                <input type="date" className={field} value={form.preferred_date} onChange={set("preferred_date")} data-testid="input-date" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A766C]">Service</label>
                <select className={`${field} appearance-none`} value={form.service} onChange={set("service")} data-testid="input-service">
                  <option value="">Select a service…</option>
                  {ALL_SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#7A766C]">Notes</label>
                <textarea rows={3} className={`${field} resize-none`} value={form.message} onChange={set("message")} placeholder="Anything we should know?" data-testid="input-message" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-appointment"
              className="mt-10 w-full sm:w-auto rounded-full bg-[#3E3B32] px-10 py-4 text-sm text-[#F8F6F0] hover:bg-[#1A1A18] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send request"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
